import { Dispatch, MutableRefObject, SetStateAction } from 'react';

import IComposableAnalyticsCharts from './IComposableAnalyticsCharts';
import IComposableAnalyticsGlobalFilters from './IComposableAnalyticsGlobalFilters';
import IComposableChartsWidget from './IComposableChartsWidget';

export interface IComposableDashboardContext {
  loader?: boolean;
  setLoader?: Dispatch<SetStateAction<boolean>>;
  dashboards?: IComposableDashboard[];
  setDashboards?: Dispatch<SetStateAction<IComposableDashboard[]>>;
  chartProperties?: MutableRefObject<
    Record<string, Omit<IComposableAnalyticsCharts, 'filters' | 'name' | 'type' | 'layout'>>
  >;
  selectedDashboard?: IComposableDashboard;
  setSelectedDashboard?: Dispatch<SetStateAction<IComposableDashboard>>;
  addWidget?: (chartObj: IComposableChartsWidget) => void;
  removeWidget?: (chartObj: IComposableChartsWidget) => void;
  setSelectedDashboardByIndex?: (index: number) => void;
  updateTableWidget?: (widgetType: string, setting: Record<string, any>) => void;
  updateTextWidgetColor?: (widgetId: string, color: string) => void;
}

export enum ComposableDashboardFiltersEnum {
  RECRUITER = 'RECRUITER',
  CLIENT = 'CLIENT',
  ACCOUNT_MANAGER = 'ACCOUNT_MANAGER',
  STATUS = 'STATUS',
  TEAM = 'TEAM',
  STAGE_STATUS = 'STAGE_STATUS',
  FILTER_DATE_BY = 'FILTER_DATE_BY',
  JOB_STATUS = 'JOB_STATUS',
  DATE_RANGE = 'DATE_RANGE',
  BUSINESS_UNIT = 'BUSINESS_UNIT',
  BUSINESS_DEFINITION = 'BUSINESS_DEFINITION',
  JOB_TYPE = 'JOB_TYPE',
}

export const composableDashboardFiltersLabelValue: Record<
  ComposableDashboardFiltersEnum,
  {
    label: string;
    value: Partial<
      IComposableAnalyticsGlobalFilters & {
        fromDay: any;
        smartFilters: any[];
        singleDateField: boolean;
      }
    >;
  }
> = {
  [ComposableDashboardFiltersEnum.RECRUITER]: {
    label: 'Recruiter Filter',
    value: { recruiterNames: [] },
  },
  [ComposableDashboardFiltersEnum.CLIENT]: {
    label: 'Client Filter',
    value: { clientNames: [] },
  },
  [ComposableDashboardFiltersEnum.ACCOUNT_MANAGER]: {
    label: 'Account Manager Filter',
    value: { accountManagers: [] },
  },
  [ComposableDashboardFiltersEnum.STATUS]: {
    label: 'Status Filter',
    value: { status: '', candidateStageStatusPipelineData: '' },
  },
  [ComposableDashboardFiltersEnum.TEAM]: {
    label: 'Teams Filter',
    value: { teamIds: [], teamNames: [] },
  },
  [ComposableDashboardFiltersEnum.STAGE_STATUS]: {
    label: 'Stage Status Filter',
    value: { currentStageCurrentStatusPairs: [] },
  },
  [ComposableDashboardFiltersEnum.FILTER_DATE_BY]: {
    label: 'Filter Date By',
    value: { datesUnionBy: 'CREATED_BETWEEN' },
  },
  [ComposableDashboardFiltersEnum.JOB_STATUS]: {
    label: 'Job Status Filter',
    value: { jobStatus: '' },
  },
  [ComposableDashboardFiltersEnum.DATE_RANGE]: {
    label: 'Date Range',
    value: {
      fromDay: null,
      fromDate: '',
      toDate: '',
      smartFilters: [],
      singleDateField: false,
    },
  },
  [ComposableDashboardFiltersEnum.BUSINESS_DEFINITION]: {
    label: 'Business Definition Filter',
    value: { currentStageCurrentStatusPairs: [], bdId: '' },
  },
  [ComposableDashboardFiltersEnum.BUSINESS_UNIT]: {
    label: 'Business Unit Filter',
    value: {
      businessUnitId: '',
    },
  },
  [ComposableDashboardFiltersEnum.JOB_TYPE]: {
    label: 'Job Type Filter',
    value: {
      jobType: '',
    },
  },
};

export interface IDependentFilters {
  smartFilters: string[];
  singleDateField?: boolean;
}

export interface IComposableDashboardFiltersContext {
  filters?: IComposableAnalyticsGlobalFilters;
  setFilters?: Dispatch<SetStateAction<IComposableAnalyticsGlobalFilters>>;
  filtersAdddedToDashboard?: ComposableDashboardFiltersEnum[];
  addFilterToDashboard?: (filter: ComposableDashboardFiltersEnum) => void;
  removeFilterFromDashboard?: (filter: ComposableDashboardFiltersEnum) => void;
  saveAppliedFiltersPayload?: () => Record<
    ComposableDashboardFiltersEnum,
    IComposableAnalyticsGlobalFilters
  >;
  dependentFilters?: IDependentFilters;
  setDependentFilters?: Dispatch<SetStateAction<IDependentFilters>>;
  refreshFiltersState?: MutableRefObject<boolean>;
  overriddenFilters?: MutableRefObject<IComposableAnalyticsGlobalFilters>;
}
export default interface IComposableDashboard {
  name: string;
  charts: IComposableAnalyticsCharts[] | string;
  appliedDashboardFilters?: Record<
    ComposableDashboardFiltersEnum,
    IComposableAnalyticsGlobalFilters
  >;
  createdBy: string;
  createdByName: string;
  workspaceId: string;
  id?: string;
  accessLevel: string;
  sharedWithRecruiters: string[];
  sharedWithTeams: string[];
  sharedWithRoles: string[];
  createdOn: string;
  modifiedBy: string;
  modifiedOn: string;
  modifiedByName: string;
  description: string;
}
